import type {
  ActivityPageType,
  ActivityType,
  AssessmentType,
  BreakoutGroupType,
  ClassUserType,
  CourseCodeGroupingType,
  CourseCodeType,
  CourseOutcomeType,
  CourseType,
  CourseUserType,
  GroupSubmissionType,
  IndividualSubmissionType,
  OrgUserType,
  OutcomeAssessmentType,
  OutcomeGroupType,
  OutcomeSubgroupType,
  OutcomeType,
  PageGroupType,
  PageGroupUserType,
  PageOutcomeType,
  PageType,
  SubmissionType,
  TermType,
  TopicType,
  UserInformationType,
  UserType,
} from "components/server-types";
import type { WithRequired } from "utils/type-utils";

export enum CourseUserActivityStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  OFFLINE = "offline",
}

export type EnabledCourseUser = CourseUserType & { is_disabled: false };

export type EnabledOrgUser = OrgUserType & { is_disabled: false };

export type UserWithCourseUserType = UserType & CourseUserType & { course_user_id: string };

export type UserWithCourseActivity = UserWithCourseUserType & {
  courseActivityStatus: CourseUserActivityStatus;
  isFollowing: boolean;
  pageId: string;
};

// NOTE: this is used in the CourseUsersSidebarPanel where we need to sort a list of users by
// course activity status and by specific fields on the ClassUser model.
export type EnhancedUserWithCourseActivity = UserWithCourseActivity & {
  hand_raised_at: string | null;
  is_in_class_session: boolean;
};

export type OrderedCourseType = CourseType & { order: number };

export type CourseWithPresentationInfoType = CourseType & { presented_page_id: string | null };

export type CourseWithAssessmentInfoType = CourseType & {
  course_code: CourseCodeType;
  pages: PageWithAssessmentInfoType[];
};

export type ObjectWithCourses<T extends TermType | CourseCodeType = TermType> = T & {
  courses: OrderedCourseType[];
};

export type CourseCodeGroupingWithCodesType = CourseCodeGroupingType & {
  courseCodes: CourseCodeType[];
};

export type CourseCodeWithGroupingType = CourseCodeType & {
  groupingTitle: string | null;
};

export type ActivityWithPagesType = ActivityType & { pages: ActivityPageType[] };
export type TopicWithActivitiesType = TopicType & { activities: ActivityWithPagesType[] };
export type TopicWithPagesType = WithRequired<
  TopicType,
  "released_page_count" | "unreleased_page_count"
> & { pages: PageType[] };

export type OutcomeWithScoreType = OutcomeType & {
  averageScore?: number;
};

export type NestedOutcomeSubgroupType = OutcomeSubgroupType & {
  outcomes: OutcomeWithScoreType[];
  averageOutcomeScore?: number;
};

export type NestedOutcomeGroupType = OutcomeGroupType & {
  outcomes: OutcomeWithScoreType[];
  outcomeSubgroups: NestedOutcomeSubgroupType[];
  outcomeCount: number;
  averageOutcomeScore?: number;
};

export type NestedCourseOutcomeType = CourseOutcomeType & {
  outcome: OutcomeType;
};

export type NestedPageOutcomeType = PageOutcomeType & {
  course_outcome: CourseOutcomeType;
  outcome: OutcomeType;
};

export type ExtendedPageGroupUserType = PageGroupUserType &
  UserInformationType & {
    user_id: string;
  };

export type NestedPageGroupType = PageGroupType & {
  page_group_users: ExtendedPageGroupUserType[];
};

export type AvUserType = UserType &
  CourseUserType &
  ClassUserType & { course_user_id: string; class_user_id: string };

export type PageWithDueDateType = PageType & { due_at: string };

export type FullIndividualSubmissionType = IndividualSubmissionType & {
  course_user: CourseUserType;
  assessment?: AssessmentType;
  outcome_assessments?: OutcomeAssessmentType[];
};

export type FullGroupSubmissionType = GroupSubmissionType & {
  assessment?: AssessmentType;
  outcome_assessments?: OutcomeAssessmentType[];
};

export type NestedAssessmentType = AssessmentType & {
  submission: SubmissionType;
  outcome_assessments: OutcomeAssessmentType[];
};

export type PageWithAssessmentsType = PageType & {
  topic: TopicType | null;
  assessments: NestedAssessmentType[];
  nonOfficialAssessments: NestedAssessmentType[];
};

export type PagesWithOutcomeAssessmentType = PageType & {
  assessment: NestedAssessmentType;
  outcomeAssessment: OutcomeAssessmentType;
  nonOfficialAssessments: NestedAssessmentType[];
};

export type PagesWithOutcomeAssessmentAndCourseType = PagesWithOutcomeAssessmentType & {
  course_title: string;
};

export type PageWithTopicIdType = PageType & { topic_id: string | null };

export type PageWithAssessmentInfoType = PageType & {
  activity: ActivityType | null;
  topic: TopicType | null;
  page_outcomes: PageOutcomeType[];
  assessible_submissions_count: number;
  assessed_submissions_count: number;
  average_points: number | undefined;
};

export type GradeCategory = "prepwork" | "activities" | "assignments";

export type NumberedBreakoutGroupType = BreakoutGroupType & {
  number: number;
};

export type FullBreakoutGroupType = NumberedBreakoutGroupType & {
  members: ClassUserType[];
};
